import React, { useState, useRef, useEffect } from 'react';
import { useDrop } from 'react-dnd';
import styled from 'styled-components';
import Animation from './Animation'
import { motion } from "framer-motion";

const Container = styled.div`
    display: flex;
    margin: ${props => props.margin || null};
    flex-direction: ${props => !props.direction ? 'row' 
                                : props.direction === 'vertical' ? 'column'
                                : props.direction === 'horizontal' ? 'row'
                                : 'row'};
    width: ${props => props.width || '100%'};
    height: auto; // was 100% previously
    padding-right: 0.2rem;
    border-radius: ${props => props.hoverStyle === 'border' ? 0 : '5px'};
    flex-grow: 4;
    border: ${props => props.isOver && props.hoverStyle === 'borders' ? '2px solid var(--focus-color)' 
                    : props.hoverStyle === 'borders' ? '2px solid transparent'
                    : '0px solid transparent'};
    box-shadow: ${props => props.isOver && props.hoverStyle === 'border' ? '0 -1px 0px 0px var(--focus-color)' : null};
    -webkit-box-shadow: ${props => props.isOver && props.hoverStyle === 'border' ? '0 -1px 0px 0px var(--focus-color)' : null};
    -moz-box-shadow: ${props => props.isOver && props.hoverStyle === 'border' ? '0 -1px 0px 0px var(--focus-color)' : null};
    /* border: 1px solid red; */
`

const PaddingContainer = styled.div`
    width: ${props => props.xOffset === 0 && props.isOver ? 0 
                    : props.xOffset && props.isOver ? (props.xOffset * 1.5) + 'rem' 
                    : (props.secondaryIndex * 1.5) + 'rem'};
    background: ${props => props.paddingColor ? props.paddingColor : null};
`
const ContentContainer = styled.div`
    width: 100%;
    border-radius: ${props => props.hoverStyle === 'border' ? 0 : '5px'};
    background-color: ${props => !props.isDragged || props.hoverStyle === 'border' ? 'transparent'
                                : props.backgroundColor ? props.backgroundColor
                                : 'var(--secondary-color)'};
`
const Wrapper = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    opacity: ${props => props.isDragged && props.hoverStyle !== 'border' ? 0 : 1};
`

export const Droppable = (props) => {
    const {
        id,
        secID,
        accept,
        direction,
        secondaryIndex,
        secDropStatus,
        secDropIDs,
        backgroundColor,
        onHoverHandler,
        onDropHandler,
        isDragged,
        hoverStyle,
        shouldAnimate,
        animation,
        margin,
        width,
        disableDrag,
        disableShallow
    } = props;
    const ref = useRef(null);
    const [xOffset, setXOffset] = useState(secondaryIndex);
    const [dropValidity, setDropValidity] = useState(false);
    const [item, setItem] = useState(null);

    const [state,drop] = useDrop({
        accept: accept,
        collect: (monitor) => ({
            isOver: monitor.isOver({ shallow: disableShallow ? false : true }),
        }),
        hover(item,monitor) {
            if(disableDrag){ return }
            if(monitor.canDrop() & !isDragged | (accept.includes(item.type) | accept === item.type)){
                const threshold = hoverStyle === 'border' ? false : true
                // const threshold = true
                if(threshold | secID === item.secID){
                    // Perform move only after 50% for vertical and normal for horizontal
                    const dragIndex = item.props.index;
                    const hoverIndex = props.index;
                    const hoverBoundingRect = ref.current.getBoundingClientRect();
                    const clientOffset = monitor.getClientOffset();

                    // const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
                    // const hoverClientY = clientOffset.y - hoverBoundingRect.top;
                    const hoverClientX = clientOffset.x - hoverBoundingRect.left;
                    const hoverMiddleX = (hoverBoundingRect.right - hoverBoundingRect.left) / 1;
                    const hoverClientY = clientOffset.y
                    const hoverMiddleY = hoverBoundingRect.top + item.height

                    // console.log({height: item.height, top: hoverBoundingRect.top, bottom: hoverBoundingRect.bottom, y: clientOffset.y})
                    if(props.direction === 'horizontal'){
                        // Dragging leftwards
                        if (dragIndex < hoverIndex && hoverClientX < hoverMiddleX) {
                            return;
                        }
                        // Dragging rightwards
                        if (dragIndex > hoverIndex && hoverClientX > hoverMiddleX) {
                            return;
                        }
                    } else {
                        
                        // Dragging downwards
                        if (dragIndex < hoverIndex && hoverClientY < hoverBoundingRect.bottom - item.height/3) {
                            return;
                        }
                        // Dragging upwards
                        if (dragIndex > hoverIndex && hoverClientY > hoverBoundingRect.top + item.height/3) {
                            return;
                        }
                        // console.log({drag: dragIndex, target: hoverIndex, y: hoverClientY, top: hoverBoundingRect.top, bottom: hoverBoundingRect.bottom, height: item.height})

                    }
                }   

                // Hover process
                setItem(item)
                if(secDropIDs){
                    const initialPos = monitor.getInitialClientOffset()
                    const currentPos = monitor.getClientOffset()
                    var offset = currentPos.x - initialPos.x < 0 
                                ? ~~((currentPos.x - initialPos.x) / 24) - 1 + secondaryIndex
                                : ~~((currentPos.x - initialPos.x) / 24) + secondaryIndex
                    if(offset < 0){
                        offset = 0
                    } else if (offset > secDropStatus.length - 1){
                        offset = secDropStatus.length - 1
                    }
                    setXOffset(offset)
                    setDropValidity(secDropStatus[xOffset])
                }
                onHoverHandler && onHoverHandler(props.index, xOffset, id, secDropIDs, item)
            }
        },
        drop(item, monitor) {
            if(monitor.canDrop()){
                onDropHandler && onDropHandler(item.props.children.props, props.index, xOffset, id, secDropIDs ? secDropIDs.slice(0,xOffset) : null, dropValidity)
                setXOffset(null)
                setItem(null)
            }
        }
    })
    const content = <Container ref={drop} direction={direction} isOver={state.isOver} hoverStyle={hoverStyle} margin={margin} width={width}>
                        <PaddingContainer secondaryIndex={secondaryIndex} xOffset={xOffset} isOver={state.isOver}/>
                        <ContentContainer ref={ref} isOver={state.isOver} isDragged={isDragged} hoverStyle={hoverStyle}>
                            <Wrapper isOver={state.isOver} isDragged={isDragged} hoverStyle={hoverStyle}>{props.children}</Wrapper>
                        </ContentContainer>
                    </Container>
    if(animation){
        return (
            // <Container>
                <Animation key={`${id}_${animation || ''}`} animation={animation}>
                    {content}
                </Animation>
            // </Container>
        )
    } else {
        return (
            <Container ref={drop} direction={direction} isOver={state.isOver} hoverStyle={hoverStyle} margin={margin} width={width}>
                <PaddingContainer secondaryIndex={secondaryIndex} xOffset={xOffset} isOver={state.isOver}/>
                <ContentContainer ref={ref} isOver={state.isOver} isDragged={isDragged} hoverStyle={hoverStyle} backgroundColor={props.secondaryColor}>
                    <Wrapper isOver={state.isOver} isDragged={isDragged} hoverStyle={hoverStyle}>{props.children}</Wrapper>
                </ContentContainer>
            </Container>
        )
    }
    // return (<div>{props.children}</div>)
}

export default Droppable